import React from 'react';
import { ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { MemberAvatar, getFirstName } from '@/components/team/member-avatar';
import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { useIconChip } from '@/hooks/use-theme-colors';

export interface ProfessionalOption {
  providerId: string;
  name: string;
  image: string | null;
}

function Pill({
  selected,
  label,
  leading,
  brandColor,
  onPress,
}: {
  selected: boolean;
  label: string;
  leading: React.ReactNode;
  brandColor: string;
  onPress: () => void;
}) {
  return (
    <PressableScale
      haptic
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected }}
      className="h-10 flex-row items-center gap-2 rounded-full border border-neutral-200 bg-card pl-1.5 pr-3.5"
      style={[
        { borderCurve: 'continuous' },
        selected && { backgroundColor: `${brandColor}14`, borderColor: brandColor },
      ]}
    >
      {leading}
      <ThemedText
        className="font-semibold text-sm text-neutral-700"
        style={selected ? { color: brandColor } : undefined}
      >
        {label}
      </ThemedText>
    </PressableScale>
  );
}

/**
 * Fileira de profissionais da página pública: foto + primeiro nome. Com
 * `allLabel`, abre com a opção que mostra todos (`null`).
 */
export function ProfessionalPills({
  options,
  selectedId,
  onSelect,
  brandColor,
  allLabel,
}: {
  options: ProfessionalOption[];
  selectedId: string | null;
  onSelect: (providerId: string | null) => void;
  brandColor: string;
  allLabel?: string;
}) {
  const chip = useIconChip();
  return (
    <ScrollView
      keyboardShouldPersistTaps="handled"
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ gap: 8 }}
    >
      {!!allLabel && (
        <Pill
          selected={selectedId === null}
          label={allLabel}
          brandColor={brandColor}
          onPress={() => onSelect(null)}
          leading={
            <View
              className="h-6 w-6 items-center justify-center rounded-full"
              style={{ backgroundColor: chip(brandColor, '1A').bg }}
            >
              <Ionicons name="people" size={13} color={chip(brandColor, '1A').fg} />
            </View>
          }
        />
      )}
      {options.map((option) => (
        <Pill
          key={option.providerId}
          selected={selectedId === option.providerId}
          label={getFirstName(option.name)}
          brandColor={brandColor}
          onPress={() => onSelect(option.providerId)}
          leading={
            <MemberAvatar name={option.name} image={option.image} color={brandColor} size={24} />
          }
        />
      ))}
    </ScrollView>
  );
}
